// Apolo IDE — PlanTabView (sprint plan-tab).
//
// The focused content of the header "Plano" tab. When a plan is assembled the
// header opens this tab; it shows the live to-do list — what was executed (done
// ✓), what is executing now (active → spinner), and what is pending (○). Reuses
// AgentTodoList (the Cursor-style "To-do N/M" card) so the look matches the
// inline chat card exactly. PRESENTACIONAL puro — recebe os todos do planState.

import { useEffect, useState } from 'react';
import { ListTodo } from 'lucide-react';
import AgentTodoList from './AgentTodoList.jsx';

/** "1:05" (m:ss) sob 1h; "1h07" acima. PURO. */
function fmtElapsed(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}`;
  return `${m}:${String(total % 60).padStart(2, '0')}`;
}

/** Pill de tempo do plano (pedido Pedro 2026-07-11 — "faltando a pill de tempo").
 *  Só TICA quando o run está DE FATO ativo (`running`). done → total CONGELADO
 *  (doneAt−executingSince). executing MAS run idle (parado/escalou/bloqueou sem
 *  fechar o plano) → CONGELA no último tick — nunca tica num plano fantasma
 *  (bug 2026-07-11: a pill subia a 12:01 num run que já tinha ido pra idle).
 *  Sem executingSince → null (planos antigos não mostram pill errada). Estilo
 *  neutro da pill de fase (cursor-design: sem cor viva). */
function PlanTimePill({ phase, executingSince, doneAt, running = false }) {
  const live = phase === 'executing' && running && Number.isFinite(executingSince);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return undefined;
    setNow(Date.now()); // re-sincroniza ao (re)começar a ticar (evita salto negativo pós re-aprovação)
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [live]);
  if (!Number.isFinite(executingSince)) return null;
  // done → doneAt; ticando → agora; congelado (executing idle) → último `now`
  // (o interval parou em !live, então `now` guarda o instante em que parou).
  const end = phase === 'done' ? (Number.isFinite(doneAt) ? doneAt : now) : now;
  const title = phase === 'done' ? 'Duração total da execução' : live ? 'Tempo de execução' : 'Tempo até parar (pausado)';
  return (
    <span
      className="shrink-0 rounded-full border border-apolo-borderSubtle px-2 py-0.5 font-mono text-[10px] tabular-nums text-apolo-textTertiary"
      data-testid="plan-tab-time"
      title={title}
    >
      {fmtElapsed(end - executingSince)}
    </span>
  );
}

// Calm, neutral phase labels (no loud accent — Pedro reads colored chrome as an
// error). The phase comes from the pure plan reducer (planState.phase).
const PHASE_LABEL = {
  planning: 'Planejando',
  awaiting_approval: 'Aguardando aprovação',
  executing: 'Executando',
  done: 'Concluído',
};

export default function PlanTabView({ todos = [], title = '', phase, executingSince, doneAt, running = false }) {
  const has = Array.isArray(todos) && todos.length > 0;
  // "Executando" só quando o run está DE FATO ativo; executing + run idle =
  // "Pausado" (honestidade: um plano bloqueado/escalado não está "executando").
  const isPaused = phase === 'executing' && !running;
  const phaseLabel = phase ? (isPaused ? 'Pausado' : PHASE_LABEL[phase] || phase) : null;

  return (
    <div
      className="relative flex min-h-0 flex-1 flex-col overflow-y-auto px-3 pt-3"
      data-testid="plan-tab-view"
      aria-label="Plano da sessão"
    >
      <div className="mx-auto flex w-full max-w-[var(--chat-col,76rem)] flex-col gap-3 pb-3">
        <div className="flex items-center gap-2">
          <ListTodo size={15} className="shrink-0 text-apolo-textSecondary" aria-hidden="true" />
          <h2 className="min-w-0 truncate text-[14px] font-semibold text-apolo-textPrimary">
            {title || 'Plano'}
          </h2>
          <span className="ml-auto flex shrink-0 items-center gap-1.5">
            <PlanTimePill phase={phase} executingSince={executingSince} doneAt={doneAt} running={running} />
            {phaseLabel ? (
              <span
                className="shrink-0 rounded-full border border-apolo-borderSubtle px-2 py-0.5 font-mono text-[10px] text-apolo-textTertiary"
                data-testid="plan-tab-phase"
              >
                {phaseLabel}
              </span>
            ) : null}
          </span>
        </div>

        {has ? (
          <AgentTodoList todos={todos} title="To-do" />
        ) : (
          <p className="text-[13px] text-apolo-textTertiary" data-testid="plan-tab-empty">
            Nenhum plano montado ainda. Quando o agente montar um plano, os passos
            aparecem aqui — concluídos, em andamento e pendentes.
          </p>
        )}
      </div>
    </div>
  );
}
